import { Check, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { AccessUser, Department } from '../types'

type Props = {
  users: AccessUser[]
  departmentById: Map<string, Department>
  value: string[]
  onChange: (ids: string[]) => void
  lockedIds?: string[]
  className?: string
}

export function UserPicker({ users, departmentById, value, onChange, lockedIds = [], className }: Props) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const selected = new Set(value)

  const list = useMemo(() => {
    const term = q.trim().toLocaleLowerCase('tr')
    return users.filter((u) => !term || `${u.name} ${u.email} ${u.position}`.toLocaleLowerCase('tr').includes(term))
  }, [users, q])

  const toggle = (id: string) => onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id])

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('access.users.search')} aria-label={t('access.users.search')} className="pl-8" />
      </div>
      <ul className="max-h-72 divide-y overflow-y-auto scrollbar-none rounded-xl border">
        {list.map((u) => {
          const locked = lockedIds.includes(u.id)
          const on = locked || selected.has(u.id)
          return (
            <li key={u.id}>
              <button
                type="button"
                disabled={locked}
                aria-pressed={on}
                onClick={() => toggle(u.id)}
                className={cn('flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-60', on && !locked && 'bg-brand-blue/[0.05]')}
              >
                <Avatar name={u.name} className="size-8" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{u.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {u.position} · {departmentById.get(u.departmentId)?.name ?? '—'}
                  </span>
                </span>
                {locked ? (
                  <span className="text-xs text-muted-foreground">{t('access.users.alreadyAssigned')}</span>
                ) : (
                  <span className={cn('flex size-5 items-center justify-center rounded-md border', on ? 'border-brand-blue bg-brand-blue text-white' : 'border-input')}>
                    {on ? <Check className="size-3.5" /> : null}
                  </span>
                )}
              </button>
            </li>
          )
        })}
        {list.length === 0 ? <li className="p-4 text-center text-sm text-muted-foreground">{t('common.empty')}</li> : null}
      </ul>
    </div>
  )
}
